'use client';

import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'motion/react';
import { twMerge } from 'tailwind-merge';
import { faXmark } from '@icons/classic/regular';
import ConfirmationModal from './ConfirmationModal';
import { FlexContainer } from './FlexContainer';
import { isEventFromOverlayLayer } from './dismissEvents';
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetTitle } from './Sheet';
import { SheetContext } from './SheetContext';
import SheetNavigationHeader from './SheetNavigationHeader';
import { Tabs, TabsList, TabsTrigger } from './Tabs';
import type { SheetContextValue } from './SheetContext';
const SLIDE_DISTANCE = 24;
const slideVariants = {
  enter: (direction: 'next' | 'previous' | null) => ({
    x: direction === 'next' ? -SLIDE_DISTANCE : direction === 'previous' ? SLIDE_DISTANCE : 0,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: 'next' | 'previous' | null) => ({
    x: direction === 'next' ? SLIDE_DISTANCE : direction === 'previous' ? -SLIDE_DISTANCE : 0,
    opacity: 0
  })
};
interface BaseSheetWrapperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: 'default' | 'lg' | 'xlg' | 'full';
  children?: React.ReactNode | ((props: CloseRequestRenderProps) => React.ReactNode);
  footer?: React.ReactNode;
  footerClassName?: string;
  headerActions?: React.ReactNode | React.ReactNode[];
  tabs?: {
    label: string;
    value: string;
  }[];
  /** If true, disables animations. Defaults to false */
  disableAnimations?: boolean;
  /** If true, prevents the sheet from closing when clicking outside. Defaults to false (allows closing) */
  preventOutsideClose?: boolean;
  /** If enabled, shows a confirmation modal before closing */
  confirmBeforeClose?: ConfirmBeforeClose;
  /** If true, the bottom content will have padding. Defaults to true */
  paddedBottom?: boolean;
  /** If true, the top content will have padding. Defaults to true */
  paddedTop?: boolean;
  /** Handler for back button clicks */
  onNavigateBack?: () => void;
  /** Key to trigger content animation when it changes */
  contentKey?: string;
  /** Direction for slide animation ('next' slides left, 'previous' slides right) */
  direction?: 'next' | 'previous' | null;
  /** Initial tab to display */
  initialTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tab: string) => void;
  /** Accessible title for screen readers when using a custom header. Defaults to 'Sheet' */
  srTitle?: string;
}

/** Object form of `confirmBeforeClose`: the guard predicate plus optional confirmation copy. */
export interface ConfirmBeforeCloseConfig {
  when: () => boolean;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
}
export type ConfirmBeforeClose = boolean | (() => boolean) | ConfirmBeforeCloseConfig;
export interface CloseRequestRenderProps {
  requestClose: () => void;
}
interface SheetWrapperNoHeader extends BaseSheetWrapperProps {
  header?: never;
  title?: never;
  titlePrefix?: never;
  description?: never;
}
interface SheetWrapperWithHeader extends BaseSheetWrapperProps {
  header: React.ReactNode;
  title?: never;
  titlePrefix?: never;
  description?: never;
}
interface SheetWrapperWithTitleDescription extends BaseSheetWrapperProps {
  title: string;
  header?: never;
  /** Small label above the title, for the kind of thing the sheet is showing. */
  titlePrefix?: React.ReactNode;
  description?: string;
}
export type SheetWrapperProps = SheetWrapperWithHeader | SheetWrapperWithTitleDescription | SheetWrapperNoHeader;
export default function SheetWrapper(t0) {
  const $ = _c(75);
  const {
    children,
    footer,
    footerClassName,
    headerActions,
    tabs,
    size: t1,
    preventOutsideClose: t2,
    contentKey,
    direction,
    onNavigateBack,
    confirmBeforeClose: t3,
    disableAnimations,
    paddedBottom: t4,
    paddedTop: t5,
    initialTab,
    onTabChange,
    srTitle: t6,
    ...props
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  const preventOutsideClose = t2 === undefined ? false : t2;
  const confirmBeforeClose = t3 === undefined ? false : t3;
  const paddedBottom = t4 === undefined ? true : t4;
  const paddedTop = t5 === undefined ? true : t5;
  const srTitle = t6 === undefined ? "Sheet" : t6;
  const sheetContext = useContext(SheetContext);
  const contextPresentation = sheetContext?.presentation;
  const resolvedContentKey = contentKey ?? contextPresentation?.contentKey;
  const resolvedDirection = direction === undefined ? contextPresentation?.direction ?? null : direction;
  const resolvedDisableAnimations = disableAnimations === undefined ? contextPresentation?.disableAnimations ?? false : disableAnimations;
  const resolvedInitialTab = initialTab ?? contextPresentation?.initialTab;
  let t7;
  bb0: {
    if (!sheetContext) {
      t7 = null;
      break bb0;
    }
    let t8;
    if ($[0] !== sheetContext.canNavigateBack || $[1] !== sheetContext.navigateBack) {
      t8 = {
        canNavigateBack: sheetContext.canNavigateBack,
        navigateBack: sheetContext.navigateBack,
        presentation: undefined
      };
      $[0] = sheetContext.canNavigateBack;
      $[1] = sheetContext.navigateBack;
      $[2] = t8;
    } else {
      t8 = $[2];
    }
    t7 = t8;
  }
  const childSheetContext = t7;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingExit, setPendingExit] = useState("close");
  const navigateBack = onNavigateBack ?? (sheetContext?.canNavigateBack ? sheetContext.navigateBack : undefined);
  const [selectedTab, setSelectedTab] = useState(resolvedInitialTab ?? tabs?.[0]?.value);
  const confirmationConfig = typeof confirmBeforeClose === "object" ? confirmBeforeClose : undefined;
  const confirmationTitle = confirmationConfig?.title ?? "Unsaved changes";
  const confirmationDescription = confirmationConfig?.description ?? "You have unsaved changes. Are you sure you want to close?";
  const confirmButtonText = confirmationConfig?.confirmText ?? "Close anyway";
  const cancelButtonText = confirmationConfig?.cancelText ?? "Keep editing";
  useEffect(() => {
    if (!props.open) {
      setShowConfirmation(false);
    }
  }, [props.open]);
  let t8;
  if ($[3] !== contextPresentation || $[4] !== onTabChange) {
    t8 = function handleTabChange(value) {
      setSelectedTab(value);
      contextPresentation?.onTabChange(value);
      onTabChange?.(value);
    };
    $[3] = contextPresentation;
    $[4] = onTabChange;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const handleTabChange = t8;
  const exit = function exit(kind) {
    if (kind === "back") {
      navigateBack?.();
    } else {
      props.onOpenChange(false);
    }
  };
  const requestExit = function requestExit(kind_0, event) {
    const shouldConfirm = typeof confirmBeforeClose === "object" ? confirmBeforeClose.when() : typeof confirmBeforeClose === "function" ? confirmBeforeClose() : confirmBeforeClose;
    event?.preventDefault();
    if (shouldConfirm && !showConfirmation) {
      setPendingExit(kind_0);
      setShowConfirmation(true);
    } else {
      if (!shouldConfirm) {
        exit(kind_0);
      }
    }
  };
  const handleCloseAttempt = function handleCloseAttempt(event_0) {
    requestExit("close", event_0);
  };
  const handleOpenChange = function handleOpenChange(open) {
    if (open) {
      props.onOpenChange(true);
    } else {
      if (confirmBeforeClose) {
        handleCloseAttempt();
      } else {
        props.onOpenChange(false);
      }
    }
  };
  const handleConfirmClose = async function handleConfirmClose() {
    setShowConfirmation(false);
    exit(pendingExit);
  };
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = function handleCancelClose() {
      setShowConfirmation(false);
    };
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const handleCancelClose = t9;
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function handleOpenAutoFocus(event_1) {
      event_1.preventDefault();
      event_1.stopPropagation();
    };
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const handleOpenAutoFocus = t10;
  const handleInteractOutside = preventOutsideClose ? _temp : confirmBeforeClose ? handleCloseAttempt : undefined;
  const onHandleInteractOutside = function onHandleInteractOutside(event_3) {
    if (isEventFromOverlayLayer(event_3)) {
      event_3.preventDefault();
      return;
    }
    handleInteractOutside?.(event_3);
  };
  const handleEscapeKeyDown = confirmBeforeClose ? handleCloseAttempt : undefined;
  const t11 = SheetContext;
  const T0 = Sheet;
  const t12 = props.open;
  const T1 = SheetContent;
  const t13 = props.description;
  const t14 = "right";
  let t15;
  if ($[8] !== headerActions) {
    t15 = headerActions && (Array.isArray(headerActions) ? headerActions.map(_temp2) : headerActions);
    $[8] = headerActions;
    $[9] = t15;
  } else {
    t15 = $[9];
  }
  let t16;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SheetClose><FontAwesomeIcon icon={faXmark} /><span className="sr-only">Close</span></SheetClose>;
    $[10] = t16;
  } else {
    t16 = $[10];
  }
  let t17;
  if ($[11] !== t15) {
    t17 = <FlexContainer className="absolute top-6.5 right-8 gap-1" items="center">{t15}{t16}</FlexContainer>;
    $[11] = t15;
    $[12] = t17;
  } else {
    t17 = $[12];
  }
  const T2 = AnimatePresence;
  const t18 = "wait";
  const t19 = motion;
  const t20 = "h-full flex flex-col";
  const t21 = "center";
  const t22 = "exit";
  const t23 = "enter";
  let t24;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      duration: 0.15,
      ease: "easeInOut"
    };
    $[13] = t24;
  } else {
    t24 = $[13];
  }
  const t25 = slideVariants;
  const T3 = Tabs;
  const t26 = "header" in props ? <>{props.header}<SheetTitle className="sr-only">{srTitle}</SheetTitle></> : props.title || props.description ? <SheetNavigationHeader description={props.description} onNavigateBack={navigateBack && confirmBeforeClose ? () => requestExit("back") : onNavigateBack} title={props.title} titlePrefix={props.titlePrefix} /> : <SheetTitle className="sr-only">{srTitle}</SheetTitle>;
  let t27;
  if ($[14] !== tabs) {
    t27 = tabs && <TabsList className="px-6">{tabs.map(_temp3)}</TabsList>;
    $[14] = tabs;
    $[15] = t27;
  } else {
    t27 = $[15];
  }
  const T4 = FlexContainer;
  const t28 = "overflow-x-clip overflow-y-auto w-full flex-1";
  const t29 = "column";
  const t30 = true;
  const t31 = paddedBottom ? "pb-4 sm:pb-8" : "pb-0";
  const t32 = paddedTop ? undefined : "pt-0 sm:pt-0";
  let t33;
  if ($[16] !== t31 || $[17] !== t32) {
    t33 = twMerge("sm:p-8 p-4 w-full flex flex-col grow", t31, t32);
    $[16] = t31;
    $[17] = t32;
    $[18] = t33;
  } else {
    t33 = $[18];
  }
  const t34 = typeof children === "function" ? children({
    requestClose: () => handleCloseAttempt()
  }) : children;
  let t35;
  if ($[19] !== t33 || $[20] !== t34) {
    t35 = <div className={t33}>{t34}</div>;
    $[19] = t33;
    $[20] = t34;
    $[21] = t35;
  } else {
    t35 = $[21];
  }
  let t36;
  if ($[22] !== T4 || $[23] !== t35) {
    t36 = <T4 className={t28} direction={t29} grow={t30}>{t35}</T4>;
    $[22] = T4;
    $[23] = t35;
    $[24] = t36;
  } else {
    t36 = $[24];
  }
  let t37;
  if ($[25] !== T3 || $[26] !== handleTabChange || $[27] !== selectedTab || $[28] !== t26 || $[29] !== t27 || $[30] !== t36) {
    t37 = <T3 onValueChange={handleTabChange} value={selectedTab}>{t26}{t27}{t36}</T3>;
    $[25] = T3;
    $[26] = handleTabChange;
    $[27] = selectedTab;
    $[28] = t26;
    $[29] = t27;
    $[30] = t36;
    $[31] = t37;
  } else {
    t37 = $[31];
  }
  let t38;
  if ($[32] !== footer || $[33] !== footerClassName) {
    t38 = footer && <SheetFooter className={twMerge("py-6 px-8", footerClassName)}>{footer}</SheetFooter>;
    $[32] = footer;
    $[33] = footerClassName;
    $[34] = t38;
  } else {
    t38 = $[34];
  }
  let t39;
  if ($[35] !== resolvedContentKey || $[36] !== resolvedDirection || $[37] !== t19.div || $[38] !== t24 || $[39] !== t25 || $[40] !== t37 || $[41] !== t38) {
    t39 = <t19.div className={t20} animate={t21} custom={resolvedDirection} exit={t22} initial={t23} key={resolvedContentKey} transition={t24} variants={t25}>{t37}{t38}</t19.div>;
    $[35] = resolvedContentKey;
    $[36] = resolvedDirection;
    $[37] = t19.div;
    $[38] = t24;
    $[39] = t25;
    $[40] = t37;
    $[41] = t38;
    $[42] = t39;
  } else {
    t39 = $[42];
  }
  let t40;
  if ($[43] !== T2 || $[44] !== resolvedDirection || $[45] !== t39) {
    t40 = <T2 custom={resolvedDirection} mode={t18}>{t39}</T2>;
    $[43] = T2;
    $[44] = resolvedDirection;
    $[45] = t39;
    $[46] = t40;
  } else {
    t40 = $[46];
  }
  let t41;
  if ($[47] !== T1 || $[48] !== handleEscapeKeyDown || $[49] !== handleOpenAutoFocus || $[50] !== onHandleInteractOutside || $[51] !== props || $[52] !== resolvedDisableAnimations || $[53] !== size || $[54] !== t17 || $[55] !== t40) {
    t41 = <T1 aria-describedby={t13} disableAnimations={resolvedDisableAnimations} onEscapeKeyDown={handleEscapeKeyDown} onInteractOutside={onHandleInteractOutside} onOpenAutoFocus={handleOpenAutoFocus} side={t14} size={size}>{t17}{t40}</T1>;
    $[47] = T1;
    $[48] = handleEscapeKeyDown;
    $[49] = handleOpenAutoFocus;
    $[50] = onHandleInteractOutside;
    $[51] = props;
    $[52] = resolvedDisableAnimations;
    $[53] = size;
    $[54] = t17;
    $[55] = t40;
    $[56] = t41;
  } else {
    t41 = $[56];
  }
  let t42;
  if ($[57] !== T0 || $[58] !== handleOpenChange || $[59] !== props || $[60] !== t41) {
    t42 = <T0 onOpenChange={handleOpenChange} open={t12}>{t41}</T0>;
    $[57] = T0;
    $[58] = handleOpenChange;
    $[59] = props;
    $[60] = t41;
    $[61] = t42;
  } else {
    t42 = $[61];
  }
  let t43;
  if ($[62] !== cancelButtonText || $[63] !== confirmButtonText || $[64] !== confirmationDescription || $[65] !== confirmationTitle || $[66] !== handleCancelClose || $[67] !== handleConfirmClose || $[68] !== showConfirmation) {
    t43 = <ConfirmationModal cancelButtonText={cancelButtonText} confirmButtonText={confirmButtonText} description={confirmationDescription} destructive={true} isOpen={showConfirmation} onCancel={handleCancelClose} onClose={handleCancelClose} onConfirm={handleConfirmClose} title={confirmationTitle} />;
    $[62] = cancelButtonText;
    $[63] = confirmButtonText;
    $[64] = confirmationDescription;
    $[65] = confirmationTitle;
    $[66] = handleCancelClose;
    $[67] = handleConfirmClose;
    $[68] = showConfirmation;
    $[69] = t43;
  } else {
    t43 = $[69];
  }
  let t44;
  if ($[70] !== childSheetContext || $[71] !== t11.Provider || $[72] !== t42 || $[73] !== t43) {
    t44 = <t11.Provider value={childSheetContext}>{t42}{t43}</t11.Provider>;
    $[70] = childSheetContext;
    $[71] = t11.Provider;
    $[72] = t42;
    $[73] = t43;
    $[74] = t44;
  } else {
    t44 = $[74];
  }
  return t44;
}
function _temp3(tab) {
  return <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>;
}
function _temp2(action) {
  return action;
}
function _temp(event_2) {
  return event_2.preventDefault();
}